"use client";

import { byLocale } from "@/lib/locale";
import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useLocaleRouter } from "@/lib/hooks/use-locale-router";
import Link from "@/components/ui/LocaleLink";
import { X, MagnifyingGlass, ListMagnifyingGlass } from "@phosphor-icons/react";
import { GROUP_LABELS, type Suggestion } from "@/lib/data/suggestions";
import type { SuggestionCounts, SpecialtySuggestion } from "@/lib/queries/professionals";
import { useLocale } from "@/lib/hooks/use-locale";
import { stripLocalePrefix } from "@/i18n/routing";
import {
  buildSpecialtySuggestions,
  buildSpecialtyCounts,
  filterSuggestions,
  groupByType,
  getCount,
  hasCountData,
  resultLabel,
  suggestCategoryForQuery,
  normalizeText,
} from "@/lib/search-autocomplete";
import { getCategoryLabel, normalizeCategory } from "@/lib/categories";

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  // Normalise both sides so an accent-/case-insensitive match still highlights
  // (VN accents are length-preserving, so indices map 1:1 onto the original).
  const nq = normalizeText(query.trim());
  const idx = nq ? normalizeText(text).indexOf(nq) : -1;
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold text-navy">{text.slice(idx, idx + nq.length)}</span>
      {text.slice(idx + nq.length)}
    </>
  );
}

export interface SearchAutocompleteHandle {
  focus: () => void;
  getValue: () => string;
  clear: () => void;
  /**
   * Force the input to a specific value. Needed because the component is
   * uncontrolled (`defaultValue` only seeds initial state). Callers that
   * need to push a new value after mount — e.g. SearchComposer reopening
   * with a different URL-derived query — use this instead of relying on
   * `defaultValue` re-rendering, which doesn't update internal state.
   */
  setValue: (value: string) => void;
}

interface SearchAutocompleteProps {
  /** Result counts by category / location / "category|location" key */
  counts?: SuggestionCounts;
  /** Specialty suggestions from the server */
  specialties?: SpecialtySuggestion[];
  /** Initial value of the input */
  defaultValue?: string;
  /** Called whenever the input changes */
  onValueChange?: (value: string) => void;
  /** Placeholder for the input */
  placeholder?: string;
  /**
   * Called when the user picks a suggestion or submits free-text.
   * `intent: "suggestion"` → user clicked/keyboard-selected an autocomplete item.
   *   `href` is the suggestion's URL (e.g. `/search?q=...&category=...`).
   * `intent: "submit"` → user pressed Enter without selecting a suggestion.
   *   `href` is null; `query` holds the typed text.
   * `type` is the suggestion's kind (category / location / combined / specialty),
   *   present only on `"suggestion"` — it is what a caller needs to report WHICH
   *   kind of suggestion earns taps, which the href cannot be parsed for safely.
   * Default behavior (when omitted): navigate to the href on suggestion;
   * navigate to `/search?q=...` on submit.
   */
  onSelect?: (args: {
    intent: "suggestion" | "submit";
    href: string | null;
    query: string;
    type?: Suggestion["type"];
  }) => void;
  /** id used for the input + listbox aria wiring */
  id?: string;
  /**
   * Accessible name for the field, rendered as an `sr-only` <label>.
   *
   * ⛔ PASS THIS ONLY WHERE THE CALL SITE HAS NO VISIBLE LABEL OF ITS OWN.
   * The composer and the `/search` sidebar each already render a visible
   * `<label htmlFor>` above the field; adding this one there gave the input TWO
   * associated labels, and the accessible name is the CONCATENATION of all of
   * them — measured "Search Search" on the composer and "Search Search Search
   * Search" on the sidebar, which renders twice. A second label is not a
   * stronger name, it is a stutter.
   *
   * The docked mini search is the case this exists for: a floating control with
   * no surrounding context and, before this, no accessible name at all — no
   * `aria-label`, no `aria-labelledby`, no `label[for]`, just a placeholder,
   * which disappears on the first keystroke and leaves `role="combobox"`
   * unnamed. `HeroSearchInput` has always had its own sr-only label for the
   * same reason.
   */
  label?: string;
  /** Extra class name on the input */
  inputClassName?: string;
  /** Extra class name on the dropdown wrapper */
  dropdownClassName?: string;
  /**
   * Min width for the dropdown in pixels. Useful when the anchor input
   * is narrow (e.g. desktop sidebar) and the dropdown should overflow.
   * Default: dropdown matches input width.
   */
  dropdownMinWidth?: number;
  /** Auto-focus the input on mount */
  autoFocus?: boolean;
  /** Show the magnifying glass / X icon on the right of the input */
  showRightIcon?: boolean;
  /**
   * Render an "empty state" link to /search when query has no matches.
   * Default true.
   */
  showEmptyState?: boolean;
}

/**
 * Reusable search input with autocomplete dropdown. Used in the search
 * composer (mobile drawer) and the desktop /search sidebar. The home hero
 * uses its own copy (HeroSearchInput) because it has a SEARCH submit
 * button overlaid on the right and slightly different styling.
 */
const SearchAutocomplete = forwardRef<SearchAutocompleteHandle, SearchAutocompleteProps>(
  function SearchAutocomplete(
    {
      counts = {},
      specialties = [],
      defaultValue = "",
      onValueChange,
      placeholder = "Service, specialty…",
      onSelect,
      id = "search-autocomplete",
      label,
      inputClassName,
      dropdownClassName,
      dropdownMinWidth,
      autoFocus = false,
      showRightIcon = true,
      showEmptyState = true,
    },
    ref,
  ) {
    const router = useLocaleRouter();
    // The "Use our advanced search" CTA links to /search; suppress it
    // when we're already there since the link would be a no-op. Strip the
    // locale prefix so /vi/search and /fr/search are recognised, not just EN.
    const onSearchPage = stripLocalePrefix(usePathname()) === "/search";
    const locale = useLocale();
    const t = useTranslations("search");

    const [query, setQuery] = useState(defaultValue);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const emptyStateLinkRef = useRef<HTMLAnchorElement>(null);
    const dymLinkRef = useRef<HTMLAnchorElement>(null);
    // Mirror query in a ref so the imperative handle can read the latest
    // value without rebuilding on every keystroke.
    const queryRef = useRef(query);
    useEffect(() => { queryRef.current = query; }, [query]);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      getValue: () => queryRef.current,
      clear: () => {
        setQuery("");
        setOpen(false);
        setActiveIndex(-1);
        onValueChange?.("");
      },
      setValue: (value: string) => {
        // Skip onValueChange so the parent doesn't echo our setter back —
        // this method is for parent → input sync, not input → parent.
        setQuery(value);
        setOpen(false);
        setActiveIndex(-1);
      },
    }), [onValueChange]);

    const specSuggestions = useMemo(() => buildSpecialtySuggestions(specialties), [specialties]);
    const specCounts = useMemo(() => buildSpecialtyCounts(specialties), [specialties]);

    // ⛔ An empty counts map means the fetch FAILED, not that the directory is
    // empty — see `hasCountData`. Unknown counts must not filter (that would
    // hide every suggestion) and must not badge (that would claim "0 results"
    // about the directory on the strength of a failed request).
    const knowCounts = hasCountData(counts, specCounts);
    const results = useMemo(() => {
      const allMatches = filterSuggestions(query, locale, specSuggestions);
      return hasCountData(counts, specCounts)
        ? allMatches.filter((s) => getCount(s, counts, specCounts) > 0)
        : allMatches;
    }, [query, locale, counts, specSuggestions, specCounts]);

    const groups = groupByType(results);
    const hasQuery = query.trim().length > 0;
    const hasResults = results.length > 0;
    const showEmpty = showEmptyState && open && hasQuery && !hasResults;
    const showDropdown = (open && hasResults) || showEmpty;

    // "Did you mean" — nearest category to a typo'd query, for the empty state.
    const dymCategory = showEmpty
      ? normalizeCategory(suggestCategoryForQuery(query, locale, counts) ?? "")
      : "";

    // Close on outside click
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (!containerRef.current?.contains(e.target as Node)) {
          setOpen(false);
          setActiveIndex(-1);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Scroll active item into view
    useEffect(() => {
      if (activeIndex < 0 || !listRef.current) return;
      // Scoped to our own list, not `document` — see the `optionId` note. The
      // ids are internal slugs (`cat-legal`, `loc-ha-noi`), so they are safe to
      // interpolate into a selector.
      const active = results[activeIndex];
      const el = active ? listRef.current.querySelector<HTMLElement>(`[id="${id}-${active.id}"]`) : null;
      el?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, results, id]);

    // Reset active index when query changes
    useEffect(() => {
      setActiveIndex(-1);
    }, [query]);

    // Auto-focus on mount
    useEffect(() => {
      if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    const handleSelect = useCallback(
      (suggestion: Suggestion) => {
        setQuery("");
        setOpen(false);
        setActiveIndex(-1);
        onValueChange?.("");
        if (onSelect) {
          onSelect({
            intent: "suggestion",
            href: suggestion.href,
            query: byLocale(locale, suggestion.label),
            type: suggestion.type,
          });
        } else {
          router.push(suggestion.href);
        }
      },
      [router, onSelect, onValueChange, locale],
    );

    const handleSubmit = useCallback(
      (e?: React.FormEvent) => {
        e?.preventDefault();
        const trimmed = query.trim();
        setOpen(false);
        setActiveIndex(-1);
        if (onSelect) {
          onSelect({ intent: "submit", href: null, query: trimmed });
        } else if (trimmed) {
          router.push(`/search?q=${encodeURIComponent(trimmed)}`);
        } else {
          router.push("/search");
        }
      },
      [query, onSelect, router],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && (!showDropdown || activeIndex < 0)) {
          e.preventDefault();
          handleSubmit();
          return;
        }
        if (!showDropdown) return;

        if (!hasResults) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            // First action: "Did you mean" if present, else advanced-search
            // (the latter is absent on /search, where dym is the only action).
            (dymLinkRef.current ?? emptyStateLinkRef.current)?.focus();
          } else if (e.key === "Escape") {
            // Stop propagation so a parent (e.g. SearchComposer) doesn't
            // close on the same Escape — first Esc collapses the dropdown,
            // second Esc closes the parent. The App Router roots React AT the
            // document, so a parent's document listener needs
            // stopImmediatePropagation too (as Select does).
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setOpen(false);
          }
          return;
        }

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
            break;
          case "ArrowUp":
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
            break;
          case "Enter":
            if (activeIndex >= 0 && results[activeIndex]) {
              e.preventDefault();
              handleSelect(results[activeIndex]);
            }
            break;
          case "Home":
            e.preventDefault();
            setActiveIndex(0);
            break;
          case "End":
            e.preventDefault();
            setActiveIndex(results.length - 1);
            break;
          case "Escape":
            // Stop propagation so a parent (e.g. SearchComposer) doesn't
            // close on the same Escape — first Esc collapses the dropdown,
            // second Esc closes the parent. The App Router roots React AT the
            // document, so a parent's document listener needs
            // stopImmediatePropagation too (as Select does).
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setOpen(false);
            setActiveIndex(-1);
            break;
        }
      },
      [showDropdown, hasResults, activeIndex, results, handleSelect, handleSubmit],
    );

    /**
     * ⛔ OPTION IDS ARE NAMESPACED UNDER THIS INSTANCE'S `id`, and that is not
     * cosmetic. `aria-activedescendant` and `aria-controls` are resolved by the
     * browser and by assistive tech as `getElementById` — FIRST match in
     * document order. A bare `suggestion.id` ("cat-legal") is therefore only
     * unique while exactly one of these is mounted, and the docked mini bar
     * breaks that: it has two mount sites (the homepage's own `<MiniChat>` and
     * the marketing layout's) and BOTH copies stay in the DOM after a
     * client-side navigation — measured 2026-09-08, two `#mini-search` inputs
     * and two listboxes still present 4s after the transition settled. Which
     * one sorts first is the App Router's business, so a bare id makes the live
     * combobox point at the stale copy's list on whichever navigation happens
     * to order them the other way.
     */
    const optionId = (suggestionId: string) => `${id}-${suggestionId}`;
    const activeId = activeIndex >= 0 && results[activeIndex] ? optionId(results[activeIndex].id) : undefined;
    const listboxId = `${id}-listbox`;
    const defaultInputClasses =
      "w-full rounded-control border border-navy/15 bg-white py-[9px] pl-13 pr-[2.2rem] text-sm text-navy placeholder:text-gray-500 focus-visible:border-gold focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden";

    return (
      <div ref={containerRef} className="relative">
        <form onSubmit={handleSubmit}>
          {label && (
            <label htmlFor={id} className="sr-only">
              {label}
            </label>
          )}
          <input
            ref={inputRef}
            id={id}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              onValueChange?.(e.target.value);
            }}
            onFocus={() => {
              if (query.trim()) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={open && hasResults}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeId}
            className={inputClassName ?? defaultInputClasses}
          />

          {showRightIcon && (
            query ? (
              <button
                type="button"
                aria-label={t("clearSearch")}
                className="absolute inset-y-0 right-0 flex w-[2.2rem] items-center justify-center text-navy/40 transition-colors hover:text-navy"
                // Block any chance of the click bubbling into a parent
                // overlay's outside-click handler. Also stop the synthetic
                // mousedown so the composer's focus / blur logic can't
                // interpret the press as "user moved focus outside".
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery("");
                  setOpen(false);
                  setActiveIndex(-1);
                  onValueChange?.("");
                  inputRef.current?.focus();
                }}
              >
                <X className="h-[12px] w-[12px]" weight="bold" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="submit"
                aria-label={t("searchAria")}
                className="absolute inset-y-0 right-0 flex w-[2.2rem] items-center justify-center text-navy/40 transition-colors hover:text-navy"
              >
                <MagnifyingGlass className="h-[14px] w-[14px]" weight="bold" aria-hidden="true" />
              </button>
            )
          )}
        </form>

        {/* Dropdown */}
        <div
          className={
            dropdownClassName ??
            "absolute left-0 top-full z-50 mt-2 max-h-[320px] overflow-y-auto scrollbar-thin rounded-pop border border-card-line bg-white shadow-e4"
          }
          style={{
            opacity: showDropdown ? 1 : 0,
            transform: showDropdown ? "translateY(0)" : "translateY(-6px)",
            pointerEvents: showDropdown ? "auto" : "none",
            minWidth: dropdownMinWidth ? `${dropdownMinWidth}px` : "100%",
            transition: "opacity 120ms ease-out, transform 120ms ease-out",
          }}
        >
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={t("suggestionsAria")}
            hidden={!hasResults}
          >
            {groups.map((group) => (
              <li key={group.type} role="presentation">
                <div className="px-21 pb-3 pt-13 text-[11px] font-semibold uppercase tracking-widest text-navy/40">
                  {byLocale(locale, GROUP_LABELS[group.type])}
                </div>
                <ul role="group">
                  {group.items.map((suggestion) => {
                    const flatIdx = results.indexOf(suggestion);
                    const isActive = flatIdx === activeIndex;
                    return (
                      <li
                        key={suggestion.id}
                        id={optionId(suggestion.id)}
                        role="option"
                        aria-selected={isActive}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(suggestion);
                        }}
                        onMouseEnter={() => setActiveIndex(flatIdx)}
                        className={[
                          "flex min-h-[44px] cursor-pointer items-center gap-13 px-21 py-[10px] text-sm transition-colors",
                          isActive
                            ? "bg-navy/5 text-navy"
                            : "text-gray-600 hover:bg-surface",
                        ].join(" ")}
                      >
                        <span className="flex-1">
                          <HighlightMatch text={byLocale(locale, suggestion.label)} query={query} />
                        </span>
                        {knowCounts && (
                          <span className="shrink-0 rounded-md bg-navy/5 px-[6px] py-2 text-[10px] font-medium tracking-wider text-navy/40">
                            {resultLabel(getCount(suggestion, counts, specCounts), locale)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>

          {showEmpty && (
            <div className="px-21 py-34 text-center" role="status" aria-live="polite">
              <p className="font-serif text-[1.125rem] font-medium text-navy">
                {t("noResults")}
              </p>
              {dymCategory ? (
                <p className="mt-8 text-sm text-gray-600">
                  {t.rich("didYouMean", {
                    category: getCategoryLabel(dymCategory, locale),
                    cat: (chunks) => (
                  <Link
                    ref={dymLinkRef}
                    href={`/search?category=${dymCategory}`}
                    onClick={() => {
                      setOpen(false);
                      setQuery("");
                      onValueChange?.("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown" && !onSearchPage) {
                        e.preventDefault();
                        emptyStateLinkRef.current?.focus();
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        inputRef.current?.focus();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        setOpen(false);
                        inputRef.current?.focus();
                      }
                    }}
                    className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-2 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
                  >
                    {chunks}
                  </Link>
                    ),
                  })}
                </p>
              ) : (
                <p className="mt-8 text-sm text-gray-600">
                  {onSearchPage ? t("tryDifferentTerm") : t("tryRelated")}
                </p>
              )}
              {!onSearchPage && (
                <Link
                  ref={emptyStateLinkRef}
                  href="/search"
                  onClick={() => {
                    setOpen(false);
                    setQuery("");
                    onValueChange?.("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      // Step back to the "Did you mean" link if present, else input.
                      (dymLinkRef.current ?? inputRef.current)?.focus();
                    } else if (e.key === "Escape") {
                      // See note in handleKeyDown: stop propagation so the
                      // parent doesn't also close on the same Esc.
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      setOpen(false);
                      inputRef.current?.focus();
                    }
                  }}
                  className="mt-5 inline-flex items-center gap-8 border-b-2 border-gold pb-2 text-sm font-medium text-navy transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
                >
                  <ListMagnifyingGlass size={18} weight="regular" aria-hidden="true" />
                  <span>{t("advancedSearch")}</span>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default SearchAutocomplete;
